import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Loader2, RotateCcw, Scale, CheckCircle2, XCircle, Sparkles, Forward } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { Button } from "@/components/ui/button";
import { FIELDS, PRIORITY_META } from "@/lib/triage";
import { ElapsedTimer } from "@/components/common/ElapsedTimer";

const D = {
  hu: { url: "Bejelentett tartalom URL", desc: "Az Ön leírása" },
  en: { url: "Reported content URL", desc: "Your description" },
  de: { url: "URL des gemeldeten Inhalts", desc: "Ihre Beschreibung" },
  fr: { url: "URL du contenu signalé", desc: "Votre description" },
  cs: { url: "URL nahlášeného obsahu", desc: "Váš popis" },
};

function ReportDetails({ data, lang }) {
  const d = D[lang] || D.en;
  if (!data.content_url && !data.description) return null;
  return (
    <div className="mt-7 space-y-3 border-t border-zinc-800/70 pt-6" data-testid="decision-report-details">
      {data.content_url && (
        <div>
          <div className="text-[11px] uppercase tracking-wider text-zinc-500">{d.url}</div>
          <a href={data.content_url} target="_blank" rel="noreferrer" data-testid="decision-content-url" className="mt-0.5 block break-all text-[13px] text-blue-400 hover:underline">{data.content_url}</a>
        </div>
      )}
      {data.description && (
        <div>
          <div className="text-[11px] uppercase tracking-wider text-zinc-500">{d.desc}</div>
          <p data-testid="decision-description" className="mt-1 whitespace-pre-wrap rounded-lg border border-zinc-800 bg-zinc-950 p-3 text-[13px] leading-relaxed text-zinc-300">{data.description}</p>
        </div>
      )}
    </div>
  );
}

function AssessmentView({ a, lang }) {
  if (!a) return null;
  const title = { hu: "Az értékelés részletei", en: "Assessment details", de: "Bewertungsdetails", fr: "Détails de l'évaluation", cs: "Podrobnosti posouzení" }[lang] || "Assessment details";
  const rows = FIELDS.filter((f) => a[f.key] && (Array.isArray(a[f.key]) ? a[f.key].length : true));
  const pr = a.priority && PRIORITY_META[a.priority];
  if (!rows.length && !pr) return null;
  return (
    <div className="mt-7 border-t border-zinc-800/70 pt-6" data-testid="decision-assessment">
      <div className="mb-2 flex items-center gap-2 text-[13px] font-medium text-zinc-300">
        {title}
        {pr && <span className="rounded px-1.5 py-0.5 text-[11px] font-bold text-white" style={{ backgroundColor: pr.color }}>{pr.label}</span>}
      </div>
      <div className="space-y-2">
        {rows.map((f) => (
          <div key={f.key} className="rounded-lg border border-zinc-800 bg-zinc-950 p-3">
            <div className="text-[11px] uppercase tracking-wider text-zinc-500">{f.title}</div>
            <div className="mt-0.5 text-[13px] text-zinc-200">{Array.isArray(a[f.key]) ? a[f.key].join(", ") : a[f.key]}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AiAssessment({ ai, lang }) {
  if (!ai?.summary) return null;
  const note = { hu: "Ezt az előzetes értékelést mesterséges intelligencia készítette. A végső döntést minden esetben emberi moderátor hozza meg.", en: "This preliminary assessment was generated by artificial intelligence. The final decision is always made by a human moderator.", de: "Diese vorläufige Bewertung wurde von künstlicher Intelligenz erstellt. Die endgültige Entscheidung trifft immer ein menschlicher Moderator.", fr: "Cette évaluation préliminaire a été générée par intelligence artificielle. La décision finale est toujours prise par un modérateur humain.", cs: "Toto předběžné posouzení vytvořila umělá inteligence. Konečné rozhodnutí vždy přijímá lidský moderátor." }[lang] || "";
  return (
    <div className="mt-7 rounded-xl border border-violet-500/40 bg-violet-500/[0.07] p-5" data-testid="decision-ai-assessment">
      <div className="flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.18em] text-violet-300"><Sparkles size={15} /> AI Assessment</div>
      <div className="mt-4 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">AI Response</div>
      <p className="mt-1 text-[14px] leading-relaxed text-zinc-100" data-testid="decision-ai-response">{ai.summary}</p>
      <p className="mt-3 text-[11px] text-zinc-500">{note}</p>
    </div>
  );
}

const L = {
  hu: { title: "A bejelentés döntése", actioned: "Intézkedtünk a bejelentett tartalommal kapcsolatban.", rejected: "A jelen értékelés szerint nem indokolt intézkedés.", pending: "A bejelentés még feldolgozás alatt áll.", resubmit: "Bejelentés ismételt benyújtása", dispute: "Peren kívüli vitarendezés indítása", openThread: "Vitarendezés megnyitása", ref: "Hivatkozás", notfound: "A hivatkozás nem található.", guidelines: "A döntés az alábbi közösségi irányelveken alapul" },
  en: { title: "Report decision", actioned: "We have taken action on the reported content.", rejected: "Based on this assessment, no action is warranted.", pending: "This report is still being processed.", resubmit: "Resubmit report", dispute: "Start out-of-court dispute", openThread: "Open dispute", ref: "Reference", notfound: "Reference not found.", guidelines: "This decision is based on the following community guidelines" },
  de: { title: "Entscheidung zur Meldung", actioned: "Wir haben Maßnahmen zum gemeldeten Inhalt ergriffen.", rejected: "Nach dieser Einschätzung ist keine Maßnahme gerechtfertigt.", pending: "Diese Meldung wird noch bearbeitet.", resubmit: "Meldung erneut senden", dispute: "Außergerichtliche Streitbeilegung starten", openThread: "Streitbeilegung öffnen", ref: "Referenz", notfound: "Referenz nicht gefunden.", guidelines: "Diese Entscheidung basiert auf den folgenden Community-Richtlinien" },
  fr: { title: "Décision du signalement", actioned: "Nous avons pris des mesures concernant le contenu signalé.", rejected: "Selon cette évaluation, aucune mesure n'est justifiée.", pending: "Ce signalement est encore en cours de traitement.", resubmit: "Soumettre à nouveau", dispute: "Engager un règlement extrajudiciaire", openThread: "Ouvrir le litige", ref: "Référence", notfound: "Référence introuvable.", guidelines: "Cette décision repose sur les règles communautaires suivantes" },
  cs: { title: "Rozhodnutí o oznámení", actioned: "Ohledně nahlášeného obsahu jsme přijali opatření.", rejected: "Podle tohoto posouzení není opatření odůvodněné.", pending: "Toto oznámení se stále zpracovává.", resubmit: "Podat znovu", dispute: "Zahájit mimosoudní řešení sporu", openThread: "Otevřít spor", ref: "Reference", notfound: "Reference nenalezena.", guidelines: "Toto rozhodnutí vychází z následujících komunitních pravidel" },
};

export default function DecisionPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { lang } = useLang();
  const t = L[lang] || L.en;
  const [data, setData] = useState(undefined);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/decision/${token}`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [token]);

  const openDispute = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/decision/${token}/dispute`);
      navigate(`/dispute/${r.data.dispute_token}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-50">
      <SiteHeader />
      <div className="mx-auto max-w-2xl px-5 py-16">
        {data === undefined ? (
          <div className="grid h-40 place-items-center"><Loader2 className="animate-spin text-zinc-600" /></div>
        ) : data === null ? (
          <div className="rounded-lg border border-zinc-800 bg-[#111113] p-6 text-center text-[14px] text-zinc-400">{t.notfound}</div>
        ) : (
          <div className="relative rounded-2xl border border-zinc-800/70 bg-[#111113] p-8">
            <ElapsedTimer createdAt={data.created_at} decidedAt={data.decided_at} closed={data.closed} slaHours={data.sla_hours} lang={lang}
              className="mb-4 sm:absolute sm:right-5 sm:top-5 sm:mb-0" />
            <div className="mb-1 label-caps">{t.ref}</div>
            <div className="font-mono text-lg font-semibold text-zinc-50">{data.reference}</div>

            <div className="mt-6 flex items-start gap-3">
              {data.status === "actioned" ? <CheckCircle2 className="mt-0.5 text-emerald-400" size={22} /> :
               data.status === "rejected" ? <XCircle className="mt-0.5 text-red-400" size={22} /> : null}
              <div>
                <h1 className="text-xl font-semibold tracking-tight text-zinc-50">{t.title}</h1>
                <p className="mt-2 text-[14px] leading-relaxed text-zinc-400">
                  {data.status === "actioned" ? t.actioned : data.status === "rejected" ? t.rejected : t.pending}
                </p>
              </div>
            </div>

            <AiAssessment ai={data.ai_assessment} lang={lang} />
            {data.forwarded_to_platform && (
              <div className="mt-7 flex items-start gap-3 rounded-xl border border-emerald-500/30 bg-emerald-500/[0.07] p-4" data-testid="decision-forwarded">
                <Forward size={17} className="mt-0.5 shrink-0 text-emerald-400" />
                <p className="text-[14px] leading-relaxed text-zinc-200">{{ hu: "Bejelentését továbbítottuk az illetékes platform moderátori csapatának.", en: "Your report has been forwarded to the moderation team of the relevant platform.", de: "Ihre Meldung wurde an das Moderationsteam der zuständigen Plattform weitergeleitet.", fr: "Votre signalement a été transmis à l'équipe de modération de la plateforme concernée.", cs: "Vaše oznámení bylo předáno moderátorskému týmu příslušné platformy." }[lang] || "Your report has been forwarded to the moderation team of the relevant platform."}</p>
              </div>
            )}
            <ReportDetails data={data} lang={lang} />
            <AssessmentView a={data.assessment} lang={lang} />

            {data.guidelines?.length > 0 && (
              <div className="mt-7 border-t border-zinc-800/70 pt-6" data-testid="decision-guidelines">
                <div className="mb-2 text-[13px] font-medium text-zinc-300">{t.guidelines}</div>
                <div className="space-y-2">
                  {data.guidelines.map((g) => (
                    <div key={g.id} className="rounded-lg border border-zinc-800 bg-zinc-950 p-3">
                      <div className="flex items-center gap-1.5">
                        <span className="rounded bg-zinc-800 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-zinc-400">{g.platform}</span>
                        <span className="text-[13px] font-medium text-zinc-100">{g.title}</span>
                      </div>
                      {g.content && <p className="mt-1 whitespace-pre-wrap text-[12px] leading-relaxed text-zinc-400">{g.content}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {data.rejected && (
              <div className="mt-7 flex flex-wrap gap-3 border-t border-zinc-800/70 pt-6">
                <Button data-testid="decision-resubmit" onClick={() => navigate("/")} variant="outline" className="h-11 border-zinc-800 bg-zinc-900 text-[13px] text-zinc-200 hover:bg-zinc-800">
                  <RotateCcw size={15} /> {t.resubmit}
                </Button>
                {data.dispute_token ? (
                  <Button data-testid="decision-open-dispute" onClick={() => navigate(`/dispute/${data.dispute_token}`)} className="h-11 bg-blue-600 text-[13px] text-white hover:bg-blue-700">
                    <Scale size={15} /> {t.openThread}
                  </Button>
                ) : (
                  <Button data-testid="decision-start-dispute" onClick={openDispute} disabled={busy} className="h-11 bg-blue-600 text-[13px] text-white hover:bg-blue-700">
                    {busy ? <Loader2 size={15} className="animate-spin" /> : <Scale size={15} />} {t.dispute}
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      <SiteFooter />
    </div>
  );
}
